import { useTranslations } from '@vocab/react';
import { Bleed, Box, Drawer, Stack } from 'braid-design-system';
import type { ComponentProps } from 'react';

import { CareerFeedAIHeading } from '../../CareerFeedAIHeading';
import { GenAIChatLoadingOverlay } from '../../LoadingOverlay';
import type { CareerFeedAIAssistantContentFrameProps } from '../CareerFeedAIAssistantContentFrame/CareerFeedAIAssistantContentFrame';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { HeadMessage } from '../HeadMessage/HeadMessage';
import { ProgressBar } from '../ProgressBar/ProgressBar';

import translations from './.vocab';

import * as styles from './CareerFeedAIAssistantDrawer.css';

interface CareerFeedAIAssistantDrawerProps
  extends Pick<
    CareerFeedAIAssistantContentFrameProps,
    'headMessage' | 'errorType' | 'contentSection' | 'ctaSection' | 'isLoading'
  > {
  shouldOpen?: boolean;
  onClose?: () => void;
  progressBar?: ComponentProps<typeof ProgressBar>;
}

export const CareerFeedAIAssistantDrawer = ({
  headMessage,
  errorType,
  contentSection,
  ctaSection,
  isLoading,
  progressBar,
  shouldOpen = false,
  onClose = () => {},
}: CareerFeedAIAssistantDrawerProps) => {
  const { t } = useTranslations(translations);

  return (
    <Drawer
      title=""
      width="small"
      onClose={onClose}
      open={shouldOpen}
      id="career-feed-desktop-drawer"
    >
      <Bleed top="large">
        <CareerFeedAIHeading headingText={t('Define your preferences')} />
      </Bleed>
      <GenAIChatLoadingOverlay isGenerating={isLoading}>
        <Box
          className={styles.container}
          display="flex"
          flexGrow={1}
          paddingY="xxsmall"
        >
          <Stack space="medium">
            {progressBar && (
              <ProgressBar
                currentQuestion={progressBar.currentQuestion}
                totalQuestions={progressBar.totalQuestions}
              />
            )}
            {headMessage ? <HeadMessage message={headMessage} /> : null}
            {contentSection}
            {errorType ? <ErrorMessage errorType={errorType} /> : null}
          </Stack>
        </Box>
        <Box display={{ mobile: 'none', tablet: 'block' }} paddingTop="small">
          {ctaSection}
        </Box>
      </GenAIChatLoadingOverlay>
      <Box display={{ mobile: 'block', tablet: 'none' }} width="full">
        {ctaSection}
      </Box>
    </Drawer>
  );
};
